/**
 * LiteResults — /results/:id (protected)
 *
 * Full transparency dashboard (Plan 05c):
 *   - Header: company name, AMP headline, date sub-line
 *   - RiskBreakdown
 *   - MethodsBreakdown
 *   - CalculationDetails
 *   - Disclaimer
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getLiteValuation, downloadLiteValuationPdf } from '../services/api';
import { formatCurrency } from '../components/lite/results/formatters';
import RiskBreakdown from '../components/lite/results/RiskBreakdown';
import WeightProfilePanel from '../components/lite/results/WeightProfilePanel';
import MethodsBreakdown from '../components/lite/results/MethodsBreakdown';
import CalculationDetails from '../components/lite/results/CalculationDetails';
import './LiteResults.css';

const formatDate = (iso) => {
  try {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(iso));
  } catch (_) {
    return iso;
  }
};

function LiteResults() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    getLiteValuation(id)
      .then((res) => {
        if (!cancelled) {
          setData(res);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="lite-results-loading">
        <span
          className="lite-results-spinner"
          role="status"
          aria-label="Loading your results"
        />
        <p>Loading your results…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="lite-results-error">
        <h2>Unable to load results</h2>
        <p>We could not retrieve your valuation. It may have expired or been removed.</p>
        <Link to="/valuation">Start over</Link>
      </div>
    );
  }

  const companyName = data.input?.company?.name || 'Your business';
  const result = data.result || {};
  const amp = result.amp;
  const methods = result.methods ?? [];
  const riskBreakdown = result.riskBreakdown ?? {};
  const weightProfile = result.weightProfile ?? null;
  const adjustmentOverrideUsed = result.adjustmentOverrideUsed ?? false;
  const derivedDefaults = result.derivedDefaults ?? [];
  const diagnostics = result.diagnostics ?? [];
  const engineVersion = result.engineVersion ?? '';
  const weightedEBITDA = result.weightedEBITDA;
  const weightedSDE = result.weightedSDE;

  const hasValidAmp = Number.isFinite(amp) && amp > 0;

  const handleDownloadPdf = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await downloadLiteValuationPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'valuation-report.pdf';
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (_err) {
      setDownloadError('Could not generate the PDF — try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (!hasValidAmp) {
    return (
      <div className="lite-results-container">
        <div className="lite-results-card lite-results-incomplete-card">
          <h2 className="lite-results-incomplete-heading">
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            We couldn't calculate a reliable valuation
          </h2>
          <p className="lite-results-incomplete-body">
            {/* eslint-disable-next-line react/no-unescaped-entities */}
            The information provided wasn't enough for a dependable
            estimate. Add more financial history and try again.
          </p>
          <Link className="lite-results-incomplete-link" to="/valuation">
            Start a new valuation
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="lite-results-container">
      {/* Header card */}
      <div className="lite-results-card lite-results-header-card">
        <p className="lite-results-company">{companyName}</p>
        <p className="lite-results-amp">{formatCurrency(amp)}</p>
        <p className="lite-results-subline">
          Adjusted Market Price · calculated {formatDate(data.createdAt)}
        </p>
        <button
          className="lite-results-pdf-btn"
          onClick={handleDownloadPdf}
          disabled={downloading}
        >
          {downloading ? 'Preparing…' : 'Download PDF report'}
        </button>
        {downloadError && (
          <p className="lite-results-pdf-error">{downloadError}</p>
        )}
      </div>

      {/* Risk Breakdown */}
      <div className="lite-results-section-card">
        <RiskBreakdown
          riskBreakdown={riskBreakdown}
          adjustmentOverrideUsed={adjustmentOverrideUsed}
        />
      </div>

      {/* Weight Profile (Lite only; null for Heavy) */}
      {weightProfile && (
        <div className="lite-results-section-card">
          <WeightProfilePanel weightProfile={weightProfile} />
        </div>
      )}

      {/* Methods Breakdown */}
      <div className="lite-results-section-card">
        <MethodsBreakdown
          methods={methods}
          perMethodRationale={weightProfile ? weightProfile.perMethodRationale : {}}
        />
      </div>

      {/* Calculation Details */}
      <div className="lite-results-section-card">
        <CalculationDetails
          derivedDefaults={derivedDefaults}
          diagnostics={diagnostics}
          engineVersion={engineVersion}
          weightedEBITDA={weightedEBITDA}
          weightedSDE={weightedSDE}
        />
      </div>

      {/* Disclaimer */}
      <p className="lite-results-disclaimer">
        This is an estimate based on the information you provided, not a
        formal appraisal.
      </p>
    </div>
  );
}

export default LiteResults;
